/**
 * ag-component-loader.js — carga los componentes de Horizon solo cuando hacen falta (A Granel).
 *
 * R-C1 (rendimiento, 30/09/2026). Antes, snippets/scripts.liquid pedía ~25 módulos en todas las
 * páginas, se usaran o no. Ahora los declara en dos mapas (definidos en scripts.liquid) y este
 * módulo los importa así:
 *
 *   window.__AG_THEME_COMPONENTS__       { '@theme/x': ['selector', …] }
 *     Se importa en cuanto hay en el DOM un elemento que casa con algún selector (igual que antes).
 *     Para lo que cambia cómo se pinta la página (pases, títulos, acordeones…).
 *
 *   window.__AG_THEME_COMPONENTS_LAZY__  { '@theme/y': ['selector', …] }
 *     Se importa si hay un elemento que casa, pero solo a partir del primer gesto (pointerdown,
 *     keydown, touchstart o, con ratón, el primer mousemove) o cuando el navegador queda en reposo
 *     tras `load` (requestIdleCallback con un tope de 3 s). Para lo que solo sirve al interactuar.
 *     El mousemove (ronda 2, 30/09) es para el escritorio: el megamenú y los desplegables se abren
 *     al pasar el ratón, sin clic. En móvil no hay mousemove antes del primer toque.
 *
 * Si alguien pulsa un control de un componente diferido que aún no se ha definido, el clic o el
 * envío se retienen, se carga el módulo y se repiten (nada se pierde ni se envía a medias).
 * Las API de los componentes no cambian: esto solo decide CUÁNDO se importa cada archivo.
 *
 * Scripts propios que solo sirven al interactuar: <script type="ag/diferido" data-src="URL"></script>
 * (añade data-module si es un módulo). Se inyectan en orden en el mismo momento que lo diferido.
 *
 * API: window.agComponentLoader.load('@theme/y') → Promise (fuerza la carga de un módulo).
 */
const eagerModules = globalThis.__AG_THEME_COMPONENTS__ || {};
const lazyModules = globalThis.__AG_THEME_COMPONENTS_LAZY__ || {};
const pendingModules = new Map();
const retryState = new Map();
const MAX_IMPORT_ATTEMPTS = 2;
const RETRY_DELAY_MS = 1000;
const IDLE_TIMEOUT_MS = 3000;
const REPLAY_TIMEOUT_MS = 8000;
const GESTURES = ['pointerdown', 'keydown', 'touchstart', 'mousemove'];

let released = false;

// Módulos que crean con document.createElement() un elemento de OTRO módulo y le asignan
// propiedades antes de insertarlo (product-form y sticky-add-to-cart crean <fly-to-cart> y le
// ponen source y destination). Si ese otro módulo aún no está definido, al definirse después los
// campos de su clase pisan esas propiedades con undefined y su connectedCallback falla
// («IntersectionObserver.observe: parameter 1 is not of type 'Element'», ronda 2 de QA, 30/09).
// Por eso la dependencia se importa y se define ANTES que el módulo que la usa.
const DEPENDENCIES = {
  '@theme/product-form': ['@theme/fly-to-cart'],
  '@theme/sticky-add-to-cart': ['@theme/fly-to-cart'],
};

const importModule = (moduleName) => {
  if (pendingModules.has(moduleName)) return pendingModules.get(moduleName);
  const state = retryState.get(moduleName) || { attempts: 0 };
  if (state.attempts >= MAX_IMPORT_ATTEMPTS) return Promise.resolve();
  state.attempts += 1;
  retryState.set(moduleName, state);
  const dependencies = DEPENDENCIES[moduleName] || [];
  const promise = Promise.all(dependencies.map((dependency) => importModule(dependency)))
    .then(() => import(moduleName))
    .then((module) => {
      retryState.delete(moduleName);
      return module;
    })
    .catch((error) => {
      console.error('[A Granel] No se pudo cargar ' + moduleName, error);
      pendingModules.delete(moduleName);
      if (state.attempts < MAX_IMPORT_ATTEMPTS) {
        setTimeout(() => scan(document), RETRY_DELAY_MS);
      }
    });
  pendingModules.set(moduleName, promise);
  return promise;
};

const contains = (root, selector) => {
  try {
    if (root.nodeType === Node.ELEMENT_NODE && root.matches(selector)) return true;
    return typeof root.querySelector === 'function' && root.querySelector(selector) !== null;
  } catch (error) {
    return false;
  }
};

// Un selector que es un nombre de etiqueta ya definida no obliga a importar nada (evita definir
// dos veces un elemento que otro archivo ya cargó con otra URL). Los selectores que no son
// etiquetas (atributos, clases) siempre cuentan.
const isPending = (selector) => !customElements.get(selector);

const scanMap = (root, map) => {
  for (const [moduleName, selectors] of Object.entries(map)) {
    if (pendingModules.has(moduleName)) continue;
    if (selectors.filter(isPending).some((selector) => contains(root, selector))) importModule(moduleName);
  }
};

// Scripts diferidos por marcado: <script type="ag/diferido" data-src="…"></script> (clásico) o con
// data-module (módulo). El navegador no los pide hasta que aquí se liberan; se inyectan en orden.
const DEFERRED_SCRIPT = 'script[type="ag/diferido"][data-src]';
const injectDeferredScripts = (root) => {
  const found = [];
  if (root.nodeType === Node.ELEMENT_NODE && root.matches(DEFERRED_SCRIPT)) found.push(root);
  if (typeof root.querySelectorAll === 'function') found.push(...root.querySelectorAll(DEFERRED_SCRIPT));
  for (const placeholder of found) {
    const script = document.createElement('script');
    script.src = placeholder.dataset.src;
    if (placeholder.hasAttribute('data-module')) script.type = 'module';
    else script.async = false;
    placeholder.type = 'ag/diferido-cargado';
    placeholder.after(script);
  }
};

const scan = (root) => {
  scanMap(root, eagerModules);
  if (released) {
    scanMap(root, lazyModules);
    injectDeferredScripts(root);
  }
  if (!('popover' in HTMLElement.prototype) && contains(root, '[popover]')) {
    importModule('@theme/popover-polyfill');
  }
};

/* ---------- Liberación de lo diferido: primer gesto o reposo tras la carga ---------- */

const release = () => {
  if (released) return;
  released = true;
  for (const type of GESTURES) window.removeEventListener(type, release, true);
  scanMap(document, lazyModules);
  injectDeferredScripts(document);
};

for (const type of GESTURES) window.addEventListener(type, release, { capture: true, passive: true });

const releaseWhenIdle = () => {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(release, { timeout: IDLE_TIMEOUT_MS });
  } else {
    setTimeout(release, 1000);
  }
};

/* ---------- Reproducción de interacciones sobre componentes diferidos aún sin definir ---------- */

// Etiquetas de elementos personalizados que declaran los módulos diferidos (y los inmediatos).
const TAG_RE = /^[a-z][a-z0-9]*-[a-z0-9-]*$/;
const lazyTags = new Set(Object.values(lazyModules).flat().filter((selector) => TAG_RE.test(selector)));
const lazyTagSelector = [...lazyTags].join(',');
// Los inmediatos se importan al terminar el análisis del HTML, pero un toque muy temprano puede
// llegar antes de que su módulo termine de bajar (H-05: el «Filtro» de la colección abre un
// dialog-component). Solo cuentan como destino de on:click, no como antecesores del control.
const eagerTags = new Set(Object.values(eagerModules).flat().filter((selector) => TAG_RE.test(selector)));

// Etiquetas diferidas, aún sin definir, de las que depende el elemento (él y sus antecesores).
const undefinedLazyHosts = (element) => {
  const hosts = [];
  if (!(element instanceof Element)) return hosts;
  let node = lazyTagSelector ? element.closest(lazyTagSelector) : null;
  while (node) {
    if (!customElements.get(node.localName)) hosts.push(node.localName);
    node = node.parentElement ? node.parentElement.closest(lazyTagSelector) : null;
  }
  // Horizon también enlaza controles con un componente lejano: on:click="#id/metodo", o con el
  // antecesor más cercano que casa: on:click="etiqueta/metodo".
  const bound = element.closest('[on\\:click]');
  const target = bound && (bound.getAttribute('on:click') || '').split('/')[0];
  if (target) {
    let remote = null;
    try {
      remote = target.startsWith('#') ? document.querySelector(target) : bound.closest(target);
    } catch (error) {
      remote = null;
    }
    const tag = remote ? remote.localName : '';
    if (tag && (lazyTags.has(tag) || eagerTags.has(tag)) && !customElements.get(tag)) hosts.push(tag);
  }
  return [...new Set(hosts)];
};

const waitForHosts = (tags) => {
  release();
  const defined = Promise.all(tags.map((tag) => customElements.whenDefined(tag)));
  const settled = Promise.allSettled([...pendingModules.values()]);
  const timeout = new Promise((resolve) => setTimeout(resolve, REPLAY_TIMEOUT_MS));
  // Si todos los imports terminan (o fallan) y la etiqueta sigue sin definir, se repite igual:
  // el navegador hará lo que habría hecho sin JavaScript (p. ej., enviar el formulario).
  return Promise.race([defined, settled.then(() => undefined), timeout]);
};

// Solo controles que disparan una acción al hacer clic. Los campos de texto y los <select> no se
// retienen: el foco y el desplegable nativo funcionan igual sin el componente.
const ACTIONABLE =
  'button, [role="button"], input[type="submit"], input[type="button"], input[type="checkbox"], ' +
  'input[type="radio"], input[type="image"], label, summary, [on\\:click]';
const replaying = new WeakSet();

const holdClick = (event) => {
  if (!event.isTrusted) return;
  const origin = event.composedPath ? event.composedPath()[0] : event.target;
  if (!(origin instanceof Element)) return;
  const control = origin.closest(ACTIONABLE);
  if (!control || replaying.has(control)) return;
  // Un enlace normal navega sin necesitar el componente.
  if (control.closest('a[href]') && !control.closest('[on\\:click]')) return;
  const hosts = undefinedLazyHosts(control);
  if (!hosts.length) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  // Señal visible mientras llega el módulo (el estilo está en snippets/scripts.liquid).
  control.setAttribute('aria-busy', 'true');
  control.setAttribute('data-ag-esperando', '');
  waitForHosts(hosts).then(() => {
    control.removeAttribute('aria-busy');
    control.removeAttribute('data-ag-esperando');
    replaying.add(control);
    try {
      control.click();
    } finally {
      replaying.delete(control);
    }
  });
};

const holdSubmit = (event) => {
  if (!event.isTrusted) return;
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || replaying.has(form)) return;
  const hosts = undefinedLazyHosts(form);
  if (!hosts.length) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const submitter = event.submitter || null;
  waitForHosts(hosts).then(() => {
    replaying.add(form);
    try {
      form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
    } finally {
      replaying.delete(form);
    }
  });
};

// Escribir o elegir antes de que exista el componente: el cambio ya está hecho en el control,
// así que no se bloquea; cuando el componente existe se le vuelve a avisar una vez.
const echoed = new WeakMap();
const echoInput = (event) => {
  if (!event.isTrusted) return;
  const field = event.target;
  if (!(field instanceof Element)) return;
  const hosts = undefinedLazyHosts(field);
  if (!hosts.length) return;
  const types = echoed.get(field) || new Set();
  if (types.has(event.type)) return;
  types.add(event.type);
  echoed.set(field, types);
  waitForHosts(hosts).then(() => {
    const pending = echoed.get(field);
    if (!pending) return;
    echoed.delete(field);
    for (const type of pending) {
      if (type === 'focus') {
        if (document.activeElement === field) field.dispatchEvent(new FocusEvent('focus'));
      } else {
        field.dispatchEvent(new Event(type, { bubbles: true }));
      }
    }
  });
};

if (lazyTags.size || eagerTags.size) {
  window.addEventListener('click', holdClick, true);
  window.addEventListener('submit', holdSubmit, true);
  for (const type of ['input', 'change', 'focus']) window.addEventListener(type, echoInput, true);
}

/* ---------- Cajón del carrito diferido: no perder la apertura automática ---------- */

// Si el cajón llega a cargarse bajo demanda y alguien añade al carrito antes de que exista,
// se abre en cuanto se define, como habría hecho su propio listener. Se decide con el atributo
// auto-open que tenía el cajón EN EL MOMENTO del evento: la ficha y las tarjetas lo quitan un
// instante para no abrir el cajón (ADR-013, aviso de cesta) y aquí se respeta.
if (lazyTags.has('cart-drawer-component')) {
  let missedAdd = false;
  document.addEventListener('cart:update', () => {
    if (customElements.get('cart-drawer-component')) return;
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && drawer.hasAttribute('auto-open')) missedAdd = true;
  }, true);
  customElements.whenDefined('cart-drawer-component').then(() => {
    if (!missedAdd) return;
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  });
}

/* ---------- Arranque ---------- */

const start = () => {
  if (document.documentElement.classList.contains('shopify-design-mode')) release();
  scan(document);
  const observer = new MutationObserver((mutations) => {
    const addedRoots = new Set();
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
          addedRoots.add(node);
        }
      }
    }
    for (const root of addedRoots) {
      if ([...addedRoots].some((ancestor) => ancestor !== root && ancestor.contains?.(root))) continue;
      scan(root);
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'complete') {
    releaseWhenIdle();
  } else {
    window.addEventListener('load', releaseWhenIdle, { once: true });
  }
};

globalThis.agComponentLoader = {
  load(moduleName) {
    return importModule(moduleName);
  },
  release,
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
